import type { ActionFunctionArgs, LoaderFunctionArgs, RouteObject } from 'react-router-dom';
import { apiGet, apiPatch, apiPost } from '../../lib/apiClient.js';
import { permissionLoader } from '../../routes/permissionLoader.js';
import { CampaignErrorBoundary } from './pages/CampaignErrorBoundary.js';
import { CampaignDetailPage } from './pages/CampaignDetailPage.js';
import { CampaignListPage } from './pages/CampaignListPage.js';

export async function campaignsLoader({ request }: LoaderFunctionArgs) {
  const response = await apiGet<{ campaigns: unknown[] }>('/api/v1/campaigns', { signal: request.signal });
  return response.data;
}

export async function campaignLoader({ params, request }: LoaderFunctionArgs) {
  if (!params.campaignId) throw new Response('Missing campaign id', { status: 400 });
  const response = await apiGet(`/api/v1/campaigns/${params.campaignId}`, { signal: request.signal });
  return response.data;
}

export async function campaignAction({ params, request }: ActionFunctionArgs) {
  if (!params.campaignId) throw new Response('Missing campaign id', { status: 400 });
  const formData = await request.formData();
  const body = Object.fromEntries(formData.entries());
  try {
    const response = await apiPatch(`/api/v1/campaigns/${params.campaignId}`, body);
    return { ok: true, data: response.data };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Unable to save campaign.' };
  }
}

export const campaignsRoutes: RouteObject = {
  path: 'campaigns',
  loader: campaignsLoader,
  element: <CampaignListPage />,
  errorElement: <CampaignErrorBoundary />,
  children: [
    {
      path: 'new',
      element: <CampaignDetailPage />,
      loader: permissionLoader(['campaign.manage']),
      action: async ({ request }) => {
        const formData = await request.formData();
        const response = await apiPost('/api/v1/campaigns', Object.fromEntries(formData.entries()));
        return response.data;
      },
      errorElement: <CampaignErrorBoundary />,
    },
    {
      path: ':campaignId',
      element: <CampaignDetailPage />,
      loader: campaignLoader,
      action: campaignAction,
      errorElement: <CampaignErrorBoundary />,
      children: [
        { path: 'participants', element: <CampaignDetailPage />, loader: campaignLoader },
        { path: 'deliverables', element: <CampaignDetailPage />, loader: campaignLoader },
        { path: 'offers', element: <CampaignDetailPage />, loader: campaignLoader },
        { path: 'analytics', element: <CampaignDetailPage />, loader: campaignLoader },
        { path: 'history', element: <CampaignDetailPage />, loader: campaignLoader },
      ],
    },
  ],
};
